"use client";
import { FeedPostDto } from "@/api/posts/types";
import { FeedPostAuthor } from "../FeedPostAuthor";
import { FeedOptions } from "../FeedOptions";
import ArrowsLeftRightIcon from "@/assets/icons/arrows-left-right.svg";
import { FeedPostFooter } from "../FeedPostFooter";
import { useState } from "react";
import { FeedPostCommentsList } from "../../Comments/CommentsList/FeedPost";
import { FeedPostContent } from "../FeedPostContent";
import { useQuery } from "@tanstack/react-query";
import nftsApi from "@/api/nfts/client";
import Link from "next/link";
import { NewsFeedPostContent } from "../NewsFeed/NewsFeedPostContent";

interface Props {
  feedPost: FeedPostDto;
}

const FeedRepost = ({ feedPost }: Props) => {
  const [showReply, setShowReply] = useState(false);

  const { data: enrichedRepost } = useQuery({
    //eslint-disable-next-line
    queryKey: ["enrichedRepost", feedPost.repostId],
    queryFn: async () => {
      if (feedPost.portalMetadata) return feedPost.repost;

      const enrichedRepost = await nftsApi.enrichWithNft<FeedPostDto, "nft">(
        [feedPost.repost!],
        {
          attachedNftId: {
            name: "attachedNft",
            addressGetter: (item) => item.attachedNftId!,
          },
        },
      );
      return enrichedRepost[0];
    },
  });

  return (
    <li className="pb-4 md:pb-6 border-b border-opacityGray-300 last-of-type:border-b-0 list-none">
      <div className="flex flex-col justify-between mb-1">
        <div className="flex items-center gap-1 mb-2">
          <ArrowsLeftRightIcon className="w-6 h-6 fill-endemicGray-700 endemic:fill-primary" />
          <p className="text-endemicGray-700 tracking-[-0.32px] endemic:text-primary">
            {feedPost.author.displayName
              ? `@${feedPost.author.displayName}`
              : feedPost.author.fullName}{" "}
            Reposted
          </p>
        </div>
        <div className="flex justify-between">
          <FeedPostAuthor feedPost={feedPost} />
          <FeedOptions feedPost={feedPost} repost={enrichedRepost!} />
        </div>
      </div>
      <p className="text-body-3 leading-[22px]">{feedPost.text}</p>
      {enrichedRepost && (
        <div className="relative">
          {enrichedRepost.portalMetadata ? (
            <NewsFeedPostContent
              feedPost={enrichedRepost}
              className="p-4 my-4 ml-9 border border-opacityGray-300"
            />
          ) : (
            <>
              <FeedPostContent
                hideActions
                feedPost={enrichedRepost}
                className="p-4 my-4 ml-9 border border-opacityGray-300"
                showAttachedNftLinks={false}
              />
              <Link
                className="absolute top-0 bottom-0 left-0 right-0"
                href={`/feed/${feedPost.repostId}`}
              ></Link>
            </>
          )}
        </div>
      )}

      <FeedPostFooter feedPost={feedPost} setShowReply={setShowReply} />
      <FeedPostCommentsList
        feedPost={feedPost}
        showReply={showReply}
        setShowReply={setShowReply}
      />
    </li>
  );
};

export default FeedRepost;
